import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Property } from "@/types/property";
import { Navbar } from "@/components/Navbar";
import { PropertyMap } from "@/components/PropertyMap";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { formatPrice } from "@/lib/utils";
import { toast } from "sonner";
import axiosInstance from "@/config/axios";
import { sendMessage } from "@/api/messages";
import { scheduleVisit } from "@/api/visits";
import {
  Calendar as CalendarIcon,
  MapPin,
  Phone,
  Mail,
  Share2,
  Heart,
  Home,
  Bed,
  Bath,
  Square,
  Car,
  Trees,
  Sun,
  Wind,
  Droplets as Pool,
  Warehouse,
  Building2,
  Timer,
  Key,
  DollarSign,
  CalendarClock,
  Check,
  Wifi,
  Shield,
  Tv,
  Footprints,
  Sofa,
  UtensilsCrossed,
  Baby,
  Dog,
  Package as Warehouse2,
  Shirt,
  Home as Fence,
  Snowflake,
  Waves,
  Lightbulb,
  Lock,
  Camera,
  Gamepad,
  ParkingCircle as Parking,
  MoveVertical as Elevator,
  User,
  MessageCircle,
  Clock,
  Scale,
  Building,
  Star,
  Send,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  X,
  MessageSquare,
  Calendar
} from "lucide-react";
import { GridFSImage } from '@/components/GridFSImage';
import { useAuth } from "@/hooks/useAuth";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose
} from "@/components/ui/dialog";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BEACH_PROPERTIES } from '@/data/properties';
import { cn } from "@/lib/utils";

export function PropertyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [property, setProperty] = useState<Property | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [message, setMessage] = useState("");
  const [visitDate, setVisitDate] = useState<Date | undefined>(undefined);
  const [visitTime, setVisitTime] = useState<string>("");
  const [visitMessage, setVisitMessage] = useState("");
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [showChatDialog, setShowChatDialog] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [contactMethod, setContactMethod] = useState<'platform' | 'whatsapp' | 'call' | 'email' | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);
  
  // Nuevos estados para las características adicionales
  const [viewCount, setViewCount] = useState(0);
  const [showFinancingOptions, setShowFinancingOptions] = useState(false);
  const [showPaymentFacilities, setShowPaymentFacilities] = useState(false);
  
  // Datos de ejemplo para opciones de financiamiento
  const financingOptions = [
    { bank: "Banco de Venezuela", rate: "12%", term: "20 años", downPayment: "20%" },
    { bank: "Banesco", rate: "13.5%", term: "15 años", downPayment: "25%" },
    { bank: "Banco Mercantil", rate: "14%", term: "25 años", downPayment: "15%" },
  ];
  
  // Datos de ejemplo para facilidades de pago
  const paymentFacilities = [
    "Pago en divisas (USD, EUR)",
    "Transferencia bancaria nacional e internacional",
    "Aceptamos criptomonedas (BTC, ETH)",
    "Financiamiento directo con el propietario",
    "Planes de pago flexibles"
  ];
  
  // Función para obtener un valor seguro (con fallback)
  const getSafeValue = (value: any, fallback: any = 'No especificado') => {
    return value !== undefined && value !== null ? value : fallback;
  };

  // Función para verificar si una URL de imagen es válida
  const isValidImageUrl = (url: string) => {
    if (!url) return false;
    return url.startsWith('http://') || url.startsWith('https://');
  };

  // Función para obtener una imagen por defecto
  const getDefaultImage = () => 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3';

  // Función para transformar URLs de imágenes
  const transformImageUrl = (url: string) => {
    if (!url) return getDefaultImage();
    
    if (isValidImageUrl(url)) {
      return url;
    }
    
    // Si es una ruta relativa
    if (url.startsWith('/')) {
      return `${window.location.origin}${url}`;
    }
    
    return getDefaultImage();
  };
  
  // Procesar y filtrar imágenes de la propiedad
  const propertyImages = useMemo(() => {
    if (!property || !property.images || !Array.isArray(property.images)) {
      console.log('No hay imágenes disponibles para la propiedad:', property?._id);
      return [];
    }

    // Filtrar imágenes válidas (no vacías, no null, no undefined)
    const filtered = property.images.filter(img => 
      img && img !== 'null' && img !== 'undefined' && img !== ''
    );
    
    // Ordenar imágenes para priorizar las de /api/images/
    const sorted = [...filtered].sort((a, b) => {
      const aIsApiImage = a.includes('/api/images/');
      const bIsApiImage = b.includes('/api/images/');
      
      if (aIsApiImage && !bIsApiImage) return -1;
      if (!aIsApiImage && bIsApiImage) return 1;
      return 0;
    });
    
    console.log(`Propiedad ${property._id} - Imágenes válidas: ${sorted.length}`, sorted);
    return sorted;
  }, [property]);

  // Cálculos para estadísticas reales
  const daysOnMarket = useMemo(() => {
    if (!property?.createdAt) return 0;
    const diff = Date.now() - new Date(property.createdAt).getTime();
    return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
  }, [property?.createdAt]);

  const buyerInterest = useMemo(() => {
    if (viewCount > 200) return { label: 'Alto', value: 85 };
    if (viewCount > 50) return { label: 'Medio', value: 55 };
    return { label: 'Bajo', value: 25 };
  }, [viewCount]);

  // Estado para la imagen seleccionada
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchProperty = async () => {
      if (!id) {
        setError('ID de propiedad no proporcionado');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Intentar obtener la propiedad del servidor
        const response = await axiosInstance.get(`/api/properties/${id}`);
        
        if (response.data && response.data.property) {
          const propertyData = response.data.property;
          
          // Transformar la propiedad para que coincida con el formato esperado
          const transformedProperty = {
            ...propertyData,
            operationType: propertyData.type === 'sale' ? 'venta' : 
                          propertyData.type === 'rent' ? 'arriendo' : 'temporal',
            beds: propertyData.bedrooms || 0,
            baths: propertyData.bathrooms || 0,
            sqft: propertyData.area || 0,
            // Manejar caso donde location puede no existir o tener estructura diferente
            coordinates: propertyData.location?.coordinates || [0, 0],
            address: propertyData.location?.address || propertyData.location || 'Dirección no disponible',
            country: propertyData.location?.country || 'Venezuela',
            // Asegurarse de que images sea un array
            images: Array.isArray(propertyData.images) ? propertyData.images : 
                   (propertyData.images ? [propertyData.images] : ['https://via.placeholder.com/600x400']),
            vendedor: propertyData.seller || propertyData.vendedor
          };
          
          setProperty(transformedProperty);
          if (propertyData.views) setViewCount(propertyData.views);
        } else {
          throw new Error('Formato de respuesta inválido');
        }
      } catch (err) {
        console.error('Error loading property:', err);
        
        // Intentar obtener la propiedad del localStorage como respaldo
        const storedProperties = localStorage.getItem('properties');
        if (storedProperties) {
          try {
            const properties = JSON.parse(storedProperties);
            const found = properties.find((p: Property) => p._id === id || p.id === id);
            if (found) {
              console.log('Property found in localStorage:', found);
              setProperty(found);
              return;
            }
          } catch (parseError) {
            console.error('Error parsing stored properties:', parseError);
          }
        }

        // Si no se encuentra en localStorage, buscar en las propiedades de respaldo
        const backupProperty = BEACH_PROPERTIES.find(p => p.id === id);
        if (backupProperty) {
          console.log('Property found in backup data:', backupProperty);
          setProperty(backupProperty);
          return;
        }

        setError('No se pudo cargar la propiedad. Por favor, intente de nuevo más tarde.');
        toast.error('Error al cargar la propiedad');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProperty();
  }, [id]);

  // Asegurarse de que el índice de imagen actual sea válido
  useEffect(() => {
    if (propertyImages.length > 0 && currentImageIndex >= propertyImages.length) {
      setCurrentImageIndex(0);
    }
  }, [propertyImages, currentImageIndex]);

  // Efecto para incrementar el contador de vistas cuando se carga la propiedad
  useEffect(() => {
    if (property && !isLoading) {
      const id = property._id || property.id;
      if (!id) return;

      // Incrementar después de 3 segundos para asegurar que el usuario realmente vio la página
      const timer = setTimeout(async () => {
        try {
          const response = await axiosInstance.patch(`/api/properties/${id}/view`);
          if (response.data && typeof response.data.views === 'number') {
            setViewCount(response.data.views);
            console.log("Vista real registrada para la propiedad:", id, "Vistas:", response.data.views);
          }
        } catch (err) {
          console.error("Error al registrar vista real:", err);
        }
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [property?._id, property?.id, isLoading]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-500">Cargando propiedad...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-500 mb-4">{error || 'Propiedad no encontrada'}</p>
            <Button onClick={() => window.history.back()}>Volver</Button>
          </div>
        </div>
      </div>
    );
  }

  const amenities = property ? [
    { icon: Home, label: "Tipo", value: getSafeValue(property.propertyType) },
    { icon: Bed, label: "Dormitorios", value: getSafeValue(property.bedrooms || property.beds, 0) },
    { icon: Bath, label: "Baños", value: getSafeValue(property.bathrooms || property.baths, 0) },
    { icon: Square, label: "Área Total", value: `${getSafeValue(property.area || property.sqft, 0)} m²` },
    { icon: Car, label: "Estacionamientos", value: getSafeValue(property.parking || property.estacionamientos, 0) },
    { icon: Trees, label: "Jardín", value: property.features && property.features.includes("Áreas verdes") ? "Sí" : "No" },
    { icon: Building2, label: "Año", value: getSafeValue(property.yearBuilt) },
    { icon: Wifi, label: "Internet", value: property.features && property.features.includes("Internet de alta velocidad") ? "Sí" : "No" },
    { icon: Shield, label: "Seguridad", value: property.features && property.features.includes("Seguridad 24/7") ? "Sí" : "No" },
  ] : [];

  const featureIcons: { [key: string]: any } = {
    "Internet de alta velocidad": Wifi,
    "Seguridad 24/7": Shield,
    "TV Cable": Tv,
    "Gimnasio": Footprints,
    "Sala de estar": Sofa,
    "Cocina equipada": UtensilsCrossed,
    "Área de juegos infantiles": Baby,
    "Pet friendly": Dog,
    "Bodega": Warehouse2,
    "Lavandería": Shirt,
    "Jardín": Trees,
    "Cerca eléctrica": Fence,
    "Aire acondicionado": Snowflake,
    "Piscina": Pool,
    "Iluminación": Lightbulb,
    "Sistema de seguridad": Lock,
    "Cámaras de seguridad": Camera,
    "Sala de juegos": Gamepad,
    "Estacionamiento techado": Parking,
    "Ascensor": Elevator,
    "Áreas verdes": Trees,
  };

  const details = [
    { label: "Antigüedad", value: `${property.antiguedad || 0} años` },
    { label: "Área útil", value: `${property.areaUtil || property.area} m²` },
    { label: "Área terreno", value: `${property.areaTerreno || property.area} m²` },
    { label: "Condición", value: property.condition || "Buena" },
  ];

  const handleSendMessage = async () => {
    if (!user) {
      toast.error("Debes iniciar sesión para enviar mensajes");
      return;
    }

    if (!message.trim()) {
      toast.error("El mensaje no puede estar vacío");
      return;
    }

    if (!property) {
      toast.error("Información de la propiedad no disponible");
      return;
    }

    try {
      setIsSendingMessage(true);
      
      await sendMessage({
        receiverId: property.userId || '', // El dueño de la propiedad
        propertyId: (property._id || property.id) as string,
        content: message.trim()
      });
      
      toast.success("Mensaje enviado correctamente");
      setMessage("");
      setContactMethod(null);
      setShowChatDialog(false);
      
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Error al enviar el mensaje. Intente nuevamente.");
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleScheduleVisit = async () => {
    if (!user) {
      toast.error("Debes iniciar sesión para agendar una visita");
      return;
    }

    if (!visitDate || !visitTime) {
      toast.error("Por favor selecciona fecha y hora para la visita");
      return;
    }

    if (!property) {
      toast.error("Información de la propiedad no disponible");
      return;
    }

    try {
      setIsSendingMessage(true);
      
      await scheduleVisit({
        propertyId: (property._id || property.id) as string,
        date: visitDate.toISOString(),
        timeSlot: visitTime,
        message: visitMessage || undefined,
      });
      
      toast.success("Visita agendada correctamente");
      setVisitDate(undefined);
      setVisitTime("");
      setVisitMessage("");
      setShowScheduleDialog(false);
      
    } catch (error) {
      console.error("Error scheduling visit:", error);
      toast.error("Error al agendar la visita. Intente nuevamente.");
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleShareProperty = () => {
    if (navigator.share) {
      navigator.share({
        title: property?.title || 'Propiedad en PropiedadesLatam',
        text: `Mira esta propiedad: ${property?.title}`,
        url: window.location.href,
      })
        .then(() => console.log('Compartido exitosamente'))
        .catch((error) => console.log('Error al compartir', error));
    } else {
      // Fallback para navegadores que no soportan Web Share API
      navigator.clipboard.writeText(window.location.href)
        .then(() => toast.success('Enlace copiado al portapapeles'))
        .catch(() => toast.error('No se pudo copiar el enlace'));
    }
  };

  const handleToggleFavorite = () => {
    if (!user) {
      toast.error("Debes iniciar sesión para guardar favoritos");
      return;
    }

    setIsFavorite(!isFavorite);
    
    if (!isFavorite) {
      toast.success("Propiedad añadida a favoritos");
      // Aquí se implementaría la lógica para guardar en favoritos
    } else {
      toast.success("Propiedad eliminada de favoritos");
      // Aquí se implementaría la lógica para eliminar de favoritos
    }
  };

  // Servicios de la propiedad
  const propertyServices = property?.services || property?.features || [];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-white border-b mb-2">
        <div className="container mx-auto px-4 pt-28 pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant={property.type === "sale" ? "default" : "secondary"} className="text-xs py-0.5">
                  {property.type === "sale" ? "Venta" : "Alquiler"}
                </Badge>
                <Badge variant="outline" className="text-xs py-0.5">{property.propertyType || 'Propiedad'}</Badge>
              </div>
              <h1 className="text-xl font-semibold text-gray-900 mb-0.5">{property.title}</h1>
              <div className="flex items-center text-sm text-gray-600">
                <MapPin className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                <span className="truncate">{property.location.address}</span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-3 sm:mt-0">
              <div className="flex items-center bg-white rounded-full shadow-sm border p-1 hover:shadow-md transition-all duration-300">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className={`h-9 rounded-full px-2 sm:px-3 transition-all duration-300 hover:bg-gray-100 ${isFavorite ? 'text-red-500' : 'text-gray-600'} hover:scale-105`}
                  onClick={handleToggleFavorite}
                  title={isFavorite ? "Eliminar de favoritos" : "Añadir a favoritos"}
                >
                  <Heart className={`w-4 h-4 sm:mr-2 transition-all duration-300 ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
                  <span className="hidden sm:inline text-sm font-medium">{isFavorite ? "Guardado" : "Guardar"}</span>
                </Button>
                
                <div className="h-5 w-px bg-gray-200 mx-1"></div>
                
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-9 rounded-full px-2 sm:px-3 text-gray-600 transition-all duration-300 hover:bg-gray-100 hover:scale-105"
                  onClick={handleShareProperty}
                  title="Compartir propiedad"
                >
                  <Share2 className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline text-sm font-medium">Compartir</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
          {/* Left Column */}
          <div className="space-y-8">
            {/* Image Carousel */}
            <div className="space-y-4">
              <div className="relative group">
                <Carousel 
                  opts={{
                    align: "start",
                    loop: true,
                  }}
                  plugins={[
                    Autoplay({
                      delay: 4000,
                    }) as any,
                  ]}
                  className="w-full"
                >
                  <CarouselContent>
                    {propertyImages.length > 0 ? (
                      propertyImages.map((image, index) => (
                        <CarouselItem key={index} className="basis-full">
                          <div 
                            className="relative aspect-video overflow-hidden rounded-lg cursor-pointer"
                            onClick={() => {
                              setCurrentImageIndex(index);
                              setShowImageModal(true);
                            }}
                          >
                            <GridFSImage 
                              src={image} 
                              alt={`${property.title} - Imagen ${index + 1}`} 
                              className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                              size="xl"
                              isFeatured={true}
                              priority={index === 0}
                              propertyType={property.propertyType}
                            />
                          </div>
                        </CarouselItem>
                      ))
                    ) : (
                      <CarouselItem className="basis-full">
                        <div className="relative aspect-video overflow-hidden rounded-lg">
                          <GridFSImage 
                            src="" 
                            alt={`${property.title}`} 
                            className="w-full h-full object-cover"
                            propertyType={property.propertyType}
                            priority={true}
                          />
                        </div>
                      </CarouselItem>
                    )}
                  </CarouselContent>
                  <CarouselPrevious className="left-4 opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 hover:bg-white text-ink border-none" />
                  <CarouselNext className="right-4 opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 hover:bg-white text-ink border-none" />
                </Carousel>
                
                {/* Image Counter Badge */}
                {propertyImages.length > 0 && (
                  <div className="absolute bottom-4 right-4 bg-ink/70 text-parchment px-3 py-1 rounded-none brutalist-border-sm text-xs font-black uppercase tracking-widest z-10 pointer-events-none">
                    {propertyImages.length} fotos
                  </div>
                )}
              </div>
            </div>

            {/* Tabs Section */}
            <Tabs defaultValue="description" className="w-full">
              <TabsList className="w-full grid grid-cols-3 h-auto p-1 bg-parchment border-2 border-ink shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] rounded-none">
                <TabsTrigger 
                  value="description"
                  className="data-[state=active]:bg-ink data-[state=active]:text-parchment rounded-none font-black uppercase tracking-widest text-[10px] py-3 transition-all"
                >
                  Descripción
                </TabsTrigger>
                <TabsTrigger 
                  value="location"
                  className="data-[state=active]:bg-ink data-[state=active]:text-parchment rounded-none font-black uppercase tracking-widest text-[10px] py-3 transition-all"
                >
                  Ubicación
                </TabsTrigger>
                <TabsTrigger 
                  value="features"
                  className="data-[state=active]:bg-ink data-[state=active]:text-parchment rounded-none font-black uppercase tracking-widest text-[10px] py-3 transition-all"
                >
                  Características
                </TabsTrigger>
              </TabsList>

              <TabsContent value="description" className="mt-8 animate-in fade-in duration-500">
                {/* Descripción */}
                <div className="prose max-w-none">
                  <h3 className="text-2xl font-display font-black uppercase italic mb-6 border-b-2 border-ink pb-2">Descripción</h3>
                  <p className="text-gray-600 font-serif italic text-lg leading-relaxed whitespace-pre-line">{property.description}</p>
                </div>
              </TabsContent>

              <TabsContent value="location" className="mt-8 animate-in fade-in duration-500">
                {/* Ubicación */}
                <div className="space-y-12">
                  <h3 className="text-2xl font-display font-black uppercase italic mb-6 border-b-2 border-ink pb-2">Ubicación</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                      <div className="brutalist-border-sm bg-ink p-1 h-[500px]">
                        <PropertyMap
                          properties={[property]}
                          onMapLoad={(map) => {
                            if (property.location.coordinates) {
                              const coords = property.location.coordinates;
                              const latLng: L.LatLngExpression = Array.isArray(coords) 
                                ? [coords[0], coords[1]] 
                                : [coords.lat, coords.lng];
                              map.setView(latLng, 17);
                            }
                          }}
                          interactive={false}
                        />
                      </div>
                    </div>
                    
                    <div className="brutalist-border-sm bg-white p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <MapPin className="h-5 w-5 text-terracotta" />
                        <h3 className="font-display font-black uppercase italic text-lg">Detalles</h3>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Dirección</h4>
                          <p className="font-serif italic text-lg">{property.location.address}</p>
                        </div>
                        
                        {property.location.city && (
                          <div>
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Ciudad</h4>
                            <p className="font-serif italic text-lg">{property.location.city}</p>
                          </div>
                        )}
                        
                        <div className="pt-4 border-t border-ink/10">
                          <h4 className="font-black uppercase tracking-widest text-[10px] text-gray-400 mb-2">Entorno</h4>
                          <p className="font-serif italic text-base text-gray-600 leading-relaxed">
                            {property.descripcionZona || 
                             "Zona residencial con excelente ubicación y acceso a servicios. Cercana a centros comerciales, escuelas y transporte público."}
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-6">
                        <Button 
                          asChild
                          className="w-full brutalist-border-sm bg-terracotta hover:bg-ink text-parchment font-black uppercase tracking-widest text-xs py-6"
                        >
                          <a 
                            href={`https://www.google.com/maps/dir/?api=1&destination=${property.location.coordinates?.lat},${property.location.coordinates?.lng}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            <MapPin className="h-4 w-4 mr-2" />
                            Cómo llegar
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 bg-gray-50/50 brutalist-border-sm p-8">
                    <h3 className="font-display font-black uppercase italic text-lg mb-6">Puntos de interés cercanos</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        { icon: "🏫", name: "Escuelas", distance: "0.5 km" },
                        { icon: "🏥", name: "Hospitales", distance: "1.2 km" },
                        { icon: "🛒", name: "Supermercados", distance: "0.3 km" },
                        { icon: "🏞️", name: "Parques", distance: "0.7 km" },
                        { icon: "🚌", name: "Transporte público", distance: "0.2 km" },
                        { icon: "🍽️", name: "Restaurantes", distance: "0.4 km" },
                        { icon: "🏦", name: "Bancos", distance: "0.6 km" },
                        { icon: "🛍️", name: "Centro comercial", distance: "1.5 km" }
                      ].map((poi, index) => (
                        <div key={index} className="flex items-center p-4 bg-white border border-ink/10">
                          <span className="text-2xl mr-3">{poi.icon}</span>
                          <div>
                            <p className="font-black uppercase tracking-tight text-xs text-ink">{poi.name}</p>
                            <p className="font-serif italic text-xs text-gray-500">{poi.distance}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="features" className="mt-8 animate-in fade-in duration-500">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
                  {amenities.map((amenity) => (
                    <div key={amenity.label} className="flex items-center gap-4 group">
                      <div className="p-3 bg-parchment border-2 border-ink group-hover:bg-ink group-hover:text-parchment transition-all duration-300 rotate-3 group-hover:rotate-0">
                        <amenity.icon className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">{amenity.label}</p>
                        <p className="font-display font-black uppercase italic text-lg">{amenity.value}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {property.zonasComunes && property.zonasComunes.length > 0 && (
                  <div className="mt-16">
                    <h3 className="text-2xl font-display font-black uppercase italic mb-8 border-b-2 border-ink pb-2">Zonas Comunes</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                      {property.zonasComunes.map((zona) => {
                        const ZonaIcon = featureIcons[zona] || Check;
                        return (
                          <div key={zona} className="flex items-center gap-3 group">
                            <div className="p-2 bg-parchment border border-ink/20 group-hover:bg-ink group-hover:text-parchment transition-all duration-300">
                              <ZonaIcon className="w-4 h-4" />
                            </div>
                            <span className="font-serif italic text-lg opacity-70 group-hover:opacity-100 transition-opacity">{zona}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {propertyServices && propertyServices.length > 0 && (
                  <div className="mt-16">
                    <h3 className="text-2xl font-display font-black uppercase italic mb-8 border-b-2 border-ink pb-2">Servicios</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                      {propertyServices.map((servicio: string, index: number) => (
                        <div key={index} className="flex items-center gap-3 group">
                          <div className="p-2 bg-parchment border border-ink/20 group-hover:bg-ink group-hover:text-parchment transition-all duration-300">
                            <CheckCircle className="w-4 h-4" />
                          </div>
                          <span className="font-serif italic text-lg opacity-70 group-hover:opacity-100 transition-opacity">{servicio}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column - Contact Section */}
          <div className="space-y-6 lg:sticky lg:top-24">
            <div className="bg-white rounded-lg border p-6">
              <div className="space-y-6">
                {/* Precio y tipo de operación */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b pb-4">
                    <h3 className="text-2xl font-semibold">{formatPrice(property.price)}</h3>
                    <Badge variant={property.type === "sale" ? "default" : "secondary"}>
                      {property.type === "sale" ? "Venta" : "Alquiler"}
                    </Badge>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Precio por m²</span>
                      <span className="font-medium">
                        {formatPrice(Math.round(property.price / property.area))}
                      </span>
                    </div>
                  </div>

                  {/* Resumen de características principales */}
                  <div className="grid grid-cols-3 gap-3 py-4 border-t">
                    <div className="text-center">
                      <div className="flex items-center justify-center w-8 h-8 mx-auto mb-1">
                        <Bed className="w-5 h-5 text-gray-600" />
                      </div>
                      <p className="text-sm font-medium">{property.bedrooms}</p>
                      <p className="text-xs text-gray-600">Habitaciones</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center w-8 h-8 mx-auto mb-1">
                        <Bath className="w-5 h-5 text-gray-600" />
                      </div>
                      <p className="text-sm font-medium">{property.bathrooms}</p>
                      <p className="text-xs text-gray-600">Baños</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center w-8 h-8 mx-auto mb-1">
                        <Square className="w-5 h-5 text-gray-600" />
                      </div>
                      <p className="text-sm font-medium">{property.area}</p>
                      <p className="text-xs text-gray-600">m²</p>
                    </div>
                  </div>

                  {/* Detalles adicionales */}
                  <div className="space-y-2 border-t pt-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Año de construcción</span>
                      <span className="font-medium">{property.yearBuilt || 'No especificado'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Condición</span>
                      <span className="font-medium">{property.condition || 'No especificada'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Estacionamientos</span>
                      <span className="font-medium">{property.parking || 0}</span>
                    </div>
                  </div>
                </div>

                {/* Información del vendedor */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold mb-4">Información del contacto</h3>
                  <div className="flex flex-col sm:flex-row items-center gap-3 mb-4">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                      {property.vendedor?.avatar && (
                        property.vendedor.avatar.includes('/uploads/') || 
                        property.vendedor.avatar.includes('/api/images/') || 
                        property.vendedor.avatar.match(/^[0-9a-fA-F]{24}$/)
                      ) ? (
                        <GridFSImage
                          src={property.vendedor.avatar}
                          alt={property.vendedor.nombre}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <User className="w-8 h-8 text-primary" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-lg">{property.vendedor?.nombre || "Agente inmobiliario"}</p>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">Verificado</Badge>
                        {property.vendedor?.rating && (
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            <span className="text-sm text-gray-600">{property.vendedor.rating}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {property.vendedor?.descripcion && (
                    <p className="text-gray-600 text-sm mb-4">{property.vendedor.descripcion}</p>
                  )}

                  {/* Estadísticas del vendedor */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="text-lg font-semibold">{property.vendedor?.propiedadesVendidas || 0}</p>
                      <p className="text-xs text-gray-600">Propiedades vendidas</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="text-lg font-semibold">{property.vendedor?.anosExperiencia || 0}</p>
                      <p className="text-xs text-gray-600">Años de experiencia</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="text-lg font-semibold">{property.vendedor?.clientesSatisfechos || 0}</p>
                      <p className="text-xs text-gray-600">Clientes satisfechos</p>
                    </div>
                  </div>

                  {/* Botones de contacto */}
                  <div className="space-y-3">
                    {/* Botones principales */}
                    <div className="grid grid-cols-2 gap-3">
                      <Button 
                        className="w-full"
                        onClick={() => {
                          setContactMethod(null);
                          setShowChatDialog(true);
                        }}
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Contactar
                      </Button>
                      
                      <Button 
                        className="w-full"
                        onClick={() => {
                          if (!user) {
                            toast.error("Debes iniciar sesión para agendar una visita");
                            navigate("/login");
                            return;
                          }
                          setShowScheduleDialog(true);
                        }}
                      >
                        <CalendarClock className="w-4 h-4 mr-2" />
                        Visitar
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Horarios de atención */}
                <div className="mt-6 pt-4 border-t">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-primary" />
                    <span className="font-medium text-sm sm:text-base">Horarios de atención</span>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600">
                    Lunes a Viernes: 9:00 AM - 6:00 PM<br />
                    Sábados: 9:00 AM - 2:00 PM
                  </p>
                </div>

                {/* Especialidades del vendedor */}
                {property.vendedor?.especialidades && property.vendedor.especialidades.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="text-sm sm:text-base font-medium mb-2">Especialidades</h4>
                    <div className="flex flex-wrap gap-2">
                      {property.vendedor.especialidades.map((especialidad) => (
                        <Badge key={especialidad} variant="outline">
                          {especialidad}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Contador de vistas */}
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-primary" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5C5.63636 5 2 12 2 12C2 12 5.63636 19 12 19C18.3636 19 22 12 22 12C22 12 18.3636 5 12 5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span className="font-medium text-sm">Vistas de la propiedad</span>
                    </div>
                    <Badge variant="outline" className="text-xs">{viewCount} vistas</Badge>
                  </div>
                </div>
                
                {/* Estadísticas de la propiedad */}
                <div className="mt-4 pt-4 border-t">
                  <h4 className="font-medium text-sm mb-3">Estadísticas de la propiedad</h4>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span>Días en el mercado</span>
                        <span className="font-medium">{daysOnMarket} {daysOnMarket === 1 ? 'día' : 'días'}</span>
                      </div>
                      <Progress value={Math.min(100, (daysOnMarket / 90) * 100)} className="h-1.5" />
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span>Interés de compradores</span>
                        <span className="font-medium">{buyerInterest.label}</span>
                      </div>
                      <Progress value={buyerInterest.value} className="h-1.5" />
                    </div>
                  </div>
                </div>
                
                {/* Garantía y seguridad */}
                <div className="mt-4 pt-4 border-t">
                  <h4 className="font-medium text-sm mb-3">Garantía y seguridad</h4>
                  <div className="bg-primary/5 p-3 rounded-lg text-xs">
                    <p className="mb-2">
                      <span className="font-medium">PropiedadesLatam</span> garantiza la veracidad de la información presentada en este anuncio.
                    </p>
                    <p>
                      Todas nuestras transacciones están respaldadas por nuestro equipo legal para asegurar un proceso seguro y transparente.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de imagen completa */}
      {showImageModal && property && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setShowImageModal(false)}>
          <div className="relative w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            {/* Imagen actual */}
            <div className="relative w-full h-full flex items-center justify-center">
              {propertyImages.length > 0 ? (
                <GridFSImage
                  src={propertyImages[currentImageIndex]}
                  alt={`${property.title} - Imagen ${currentImageIndex + 1}`}
                  className="max-w-full max-h-full object-contain"
                  size="xl"
                  priority={true}
                  propertyType={property.propertyType}
                  isFeatured={true}
                />
              ) : (
                <div className="text-white text-center">
                  <p className="text-xl mb-2">No hay imágenes disponibles</p>
                </div>
              )}
            </div>
            
            {/* Controles de navegación */}
            {propertyImages.length > 1 && (
              <div className="flex items-center gap-2 mb-4">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex items-center gap-1"
                  onClick={() => setCurrentImageIndex(Math.max(0, currentImageIndex - 1))}
                  disabled={currentImageIndex === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex items-center gap-1"
                  onClick={() => setCurrentImageIndex(Math.min(propertyImages.length - 1, currentImageIndex + 1))}
                  disabled={currentImageIndex === propertyImages.length - 1}
                >
                  Siguiente
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
            
            {/* Botón de cerrar */}
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-black/50 hover:bg-black/70 text-white rounded-full"
              aria-label="Cerrar"
            >
              <X className="h-6 w-6" />
            </button>
            
            {/* Contador de imágenes */}
            {propertyImages.length > 0 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm">
                {currentImageIndex + 1} / {propertyImages.length}
              </div>
            )}
            
            {/* Miniaturas */}
            {propertyImages.length > 1 && (
              <div className="absolute bottom-16 left-0 right-0 flex justify-center gap-2 px-4 overflow-x-auto py-2">
                {propertyImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex(index);
                    }}
                    className={cn(
                      "w-16 h-16 rounded-md overflow-hidden border-2 transition-all",
                      index === currentImageIndex ? "border-white scale-110" : "border-transparent opacity-70 hover:opacity-100"
                    )}
                  >
                    <GridFSImage
                      src={image}
                      alt={`Miniatura ${index + 1}`}
                      className="w-full h-full object-cover"
                      size="md"
                      propertyType={property.propertyType}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Chat Dialog */}
      <Dialog open={showChatDialog} onOpenChange={(open) => {
        setShowChatDialog(open);
        if (!open) setContactMethod(null);
      }}>
        <DialogContent className="sm:max-w-md">
          {!contactMethod ? (
            <>
              <DialogHeader>
                <DialogTitle>Contactar al agente</DialogTitle>
                <DialogDescription>
                  Selecciona el medio por el cual prefieres comunicarte.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <Button 
                  className="w-full h-12 text-base brutalist-border-sm"
                  onClick={() => {
                    if (!user) {
                      toast.error("Debes iniciar sesión para enviar mensajes");
                      navigate("/login");
                      return;
                    }
                    setContactMethod('platform');
                  }}
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Mensaje por plataforma
                </Button>

                {property.vendedor?.telefono && (
                  <Button 
                    className="w-full h-12 text-base brutalist-border-sm bg-[#25D366] text-white hover:bg-[#128C7E] border-[#128C7E]"
                    asChild
                  >
                    <a href={`https://wa.me/${property.vendedor.telefono.replace(/\+/g, '').replace(/\s/g, '')}`} target="_blank" rel="noopener noreferrer">
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      Contactar por WhatsApp
                    </a>
                  </Button>
                )}

                {property.vendedor?.telefono && (
                  <div className="p-4 brutalist-border-sm bg-parchment">
                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Teléfono</p>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-2xl font-black italic tracking-tight">{property.vendedor.telefono}</span>
                      <Button variant="outline" size="sm" className="h-10 brutalist-border-sm" asChild>
                        <a href={`tel:${property.vendedor.telefono}`}>
                          <Phone className="w-4 h-4 mr-2" />
                          Llamar
                        </a>
                      </Button>
                    </div>
                  </div>
                )}

                {property.vendedor?.email && (
                  <div className="p-4 brutalist-border-sm bg-parchment">
                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Correo Electrónico</p>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-base font-bold italic truncate flex-1">{property.vendedor.email}</span>
                      <Button variant="outline" size="sm" className="h-10 brutalist-border-sm" asChild>
                        <a href={`mailto:${property.vendedor.email}?subject=Interés en propiedad: ${property.title}`}>
                          <Mail className="w-4 h-4 mr-2" />
                          Enviar correo
                        </a>
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0" 
                    onClick={() => setContactMethod(null)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <DialogTitle>Enviar mensaje</DialogTitle>
                </div>
                <DialogDescription>
                  Escribe tu mensaje para el propietario de esta propiedad.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <Textarea
                  placeholder="Estoy interesado en esta propiedad. Me gustaría obtener más información..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="min-h-[120px]"
                />
              </div>
              <DialogFooter>
                <Button 
                  type="submit" 
                  onClick={handleSendMessage}
                  disabled={isSendingMessage}
                >
                  {isSendingMessage ? (
                    <>Enviando...</>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Enviar mensaje
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Schedule Visit Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Agendar visita</DialogTitle>
            <DialogDescription>
              Selecciona fecha y hora para visitar esta propiedad.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Fecha</h4>
              <CalendarComponent
                mode="single"
                selected={visitDate}
                onSelect={setVisitDate}
                disabled={(date) => date < new Date() || date > new Date(new Date().setMonth(new Date().getMonth() + 2))}
                className="rounded-md border"
              />
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Hora</h4>
              <Select value={visitTime} onValueChange={setVisitTime}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona una hora" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="09:00">09:00 AM</SelectItem>
                  <SelectItem value="10:00">10:00 AM</SelectItem>
                  <SelectItem value="11:00">11:00 AM</SelectItem>
                  <SelectItem value="12:00">12:00 PM</SelectItem>
                  <SelectItem value="13:00">01:00 PM</SelectItem>
                  <SelectItem value="14:00">02:00 PM</SelectItem>
                  <SelectItem value="15:00">03:00 PM</SelectItem>
                  <SelectItem value="16:00">04:00 PM</SelectItem>
                  <SelectItem value="17:00">05:00 PM</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Mensaje (opcional)</h4>
              <Textarea
                placeholder="Información adicional para la visita..."
                value={visitMessage}
                onChange={(e) => setVisitMessage(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              type="submit" 
              onClick={handleScheduleVisit}
              disabled={isSendingMessage || !visitDate || !visitTime}
            >
              {isSendingMessage ? (
                <>Agendando...</>
              ) : (
                <>
                  <CalendarClock className="w-4 h-4 mr-2" />
                  Agendar visita
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}